// ===========================================
// Subscriptions Tab
// ===========================================
// View and manage family subscriptions

import { useState, useMemo } from 'react';
import {
  Box,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Divider,
  List,
  ListItem,
  ListItemText,
  Skeleton,
} from '@mui/material';
import {
  Pause as PauseIcon,
  PlayArrow as ResumeIcon,
  Cancel as CancelIcon,
  Visibility as ViewIcon,
  SwapHoriz as ChangeIcon,
} from '@mui/icons-material';
import DataTable, { Column } from '../../common/DataTable';
import ConfirmDialog from '../../common/ConfirmDialog';
import {
  useAdminSubscriptions,
  usePauseSubscription,
  useResumeSubscription,
  useCancelSubscription,
  useChangeSubscriptionPaymentPlan,
} from '../../../hooks/useSubscriptions';
import { useAdminPaymentPlans } from '../../../hooks/usePaymentPlans';
import {
  Subscription,
  SubscriptionStatus,
  SubscriptionFilters,
} from '../../../api/subscriptions.api';

// ===========================================
// HELPERS
// ===========================================

function formatCurrency(amount: number, currency = 'AUD'): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency,
  }).format(amount);
}

// ===========================================
// TYPES & OPTIONS
// ===========================================

const statusOptions: { value: SubscriptionStatus | ''; label: string }[] = [
  { value: '', label: 'All Statuses' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'PAUSED', label: 'Paused' },
  { value: 'PAST_DUE', label: 'Past Due' },
  { value: 'UNPAID', label: 'Unpaid' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'COMPLETED', label: 'Completed' },
];

const statusColorMap: Record<SubscriptionStatus, 'success' | 'warning' | 'error' | 'default' | 'info'> = {
  ACTIVE: 'success',
  PAUSED: 'warning',
  PAST_DUE: 'error',
  UNPAID: 'error',
  CANCELLED: 'default',
  COMPLETED: 'info',
};

// ===========================================
// COMPONENT
// ===========================================

export default function SubscriptionsTab() {
  // Filters state
  const [filters, setFilters] = useState<SubscriptionFilters>({});

  // Query data
  const { data, isLoading, error } = useAdminSubscriptions(filters);
  const { data: plansData } = useAdminPaymentPlans({ isActive: true });

  const subscriptions = data?.subscriptions ?? [];
  const paymentPlans = plansData?.paymentPlans ?? [];

  // Mutations
  const pauseMutation = usePauseSubscription();
  const resumeMutation = useResumeSubscription();
  const cancelMutation = useCancelSubscription();
  const changePlanMutation = useChangeSubscriptionPaymentPlan();

  // Detail dialog state
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);

  // Pause dialog state
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [pauseReason, setPauseReason] = useState('');

  // Cancel dialog state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelImmediately, setCancelImmediately] = useState(false);

  // Change plan dialog state
  const [changePlanDialogOpen, setChangePlanDialogOpen] = useState(false);
  const [newPaymentPlanId, setNewPaymentPlanId] = useState('');

  // Resume confirmation state
  const [resumeDialogOpen, setResumeDialogOpen] = useState(false);

  // Table columns
  const columns: Column<Subscription>[] = [
    {
      id: 'family',
      label: 'Family',
      minWidth: 180,
      format: (value) => {
        const family = value as Subscription['family'];
        return (
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {family.name}
            </Typography>
            {family.parents?.[0] && (
              <Typography variant="caption" color="text.secondary">
                {family.parents[0].User.email}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      id: 'paymentPlan',
      label: 'Plan',
      minWidth: 140,
      format: (value) => {
        const plan = value as Subscription['paymentPlan'];
        return plan?.name || '-';
      },
    },
    {
      id: 'amount',
      label: 'Amount',
      minWidth: 100,
      align: 'right',
      format: (value, row) => formatCurrency(parseFloat(String(value)), row.currency),
    },
    {
      id: 'billingDay',
      label: 'Billing Day',
      minWidth: 80,
      align: 'center',
      format: (value) => (value ? `Day ${value}` : '-'),
    },
    {
      id: 'nextBillingDate',
      label: 'Next Billing',
      minWidth: 110,
      format: (value) =>
        value ? new Date(String(value)).toLocaleDateString() : '-',
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 100,
      align: 'center',
      format: (value) => {
        const status = value as SubscriptionStatus;
        return (
          <Chip
            label={status.replace('_', ' ')}
            size="small"
            color={statusColorMap[status]}
          />
        );
      },
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 180,
      align: 'right',
      format: (_, row) => (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => handleViewDetails(row)}>
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {row.status === 'ACTIVE' && (
            <Tooltip title="Pause Subscription">
              <IconButton size="small" onClick={() => handlePauseClick(row)}>
                <PauseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {row.status === 'PAUSED' && (
            <Tooltip title="Resume Subscription">
              <IconButton size="small" color="success" onClick={() => handleResumeClick(row)}>
                <ResumeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {row.status !== 'CANCELLED' && row.status !== 'COMPLETED' && (
            <Tooltip title="Change Plan">
              <IconButton size="small" onClick={() => handleChangePlanClick(row)}>
                <ChangeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {row.status !== 'CANCELLED' && row.status !== 'COMPLETED' && (
            <Tooltip title="Cancel Subscription">
              <IconButton size="small" color="error" onClick={() => handleCancelClick(row)}>
                <CancelIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ];

  // Handlers
  const handleViewDetails = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setDetailDialogOpen(true);
  };

  const handlePauseClick = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setPauseReason('');
    setPauseDialogOpen(true);
  };

  const handleResumeClick = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setResumeDialogOpen(true);
  };

  const handleCancelClick = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setCancelReason('');
    setCancelImmediately(false);
    setCancelDialogOpen(true);
  };

  const handleChangePlanClick = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setNewPaymentPlanId('');
    setChangePlanDialogOpen(true);
  };

  const handlePauseConfirm = async () => {
    if (selectedSubscription) {
      try {
        await pauseMutation.mutateAsync({
          id: selectedSubscription.id,
          data: { reason: pauseReason || undefined },
        });
        setPauseDialogOpen(false);
      } catch {
        // Error handled by mutation
      }
    }
  };

  const handleResumeConfirm = async () => {
    if (selectedSubscription) {
      try {
        await resumeMutation.mutateAsync(selectedSubscription.id);
        setResumeDialogOpen(false);
      } catch {
        // Error handled by mutation
      }
    }
  };

  const handleCancelConfirm = async () => {
    if (selectedSubscription) {
      try {
        await cancelMutation.mutateAsync({
          id: selectedSubscription.id,
          data: {
            cancelImmediately,
            reason: cancelReason || undefined,
          },
        });
        setCancelDialogOpen(false);
      } catch {
        // Error handled by mutation
      }
    }
  };

  const handleChangePlanConfirm = async () => {
    if (selectedSubscription && newPaymentPlanId) {
      try {
        await changePlanMutation.mutateAsync({
          id: selectedSubscription.id,
          data: { paymentPlanId: newPaymentPlanId, prorate: true },
        });
        setChangePlanDialogOpen(false);
      } catch {
        // Error handled by mutation
      }
    }
  };

  // Stats summary
  const stats = useMemo(() => {
    const active = subscriptions.filter((s) => s.status === 'ACTIVE').length;
    const paused = subscriptions.filter((s) => s.status === 'PAUSED').length;
    const pastDue = subscriptions.filter((s) => s.status === 'PAST_DUE' || s.status === 'UNPAID').length;
    return { active, paused, pastDue, total: subscriptions.length };
  }, [subscriptions]);

  // Loading state
  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        View and manage family subscription billing. Subscriptions are automatically created when
        families select a payment plan.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load subscriptions. Please try again.
        </Alert>
      )}

      {/* Stats Summary */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Chip
          label={`${stats.active} Active`}
          color="success"
          variant={filters.status === 'ACTIVE' ? 'filled' : 'outlined'}
          onClick={() => setFilters({ ...filters, status: filters.status === 'ACTIVE' ? undefined : 'ACTIVE' })}
        />
        <Chip
          label={`${stats.paused} Paused`}
          color="warning"
          variant={filters.status === 'PAUSED' ? 'filled' : 'outlined'}
          onClick={() => setFilters({ ...filters, status: filters.status === 'PAUSED' ? undefined : 'PAUSED' })}
        />
        <Chip
          label={`${stats.pastDue} Past Due`}
          color="error"
          variant={filters.status === 'PAST_DUE' ? 'filled' : 'outlined'}
          onClick={() => setFilters({ ...filters, status: filters.status === 'PAST_DUE' ? undefined : 'PAST_DUE' })}
        />
        <Chip
          label={`${stats.total} Total`}
          variant="outlined"
          onClick={() => setFilters({})}
        />
      </Stack>

      {/* Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.status || ''}
            onChange={(e) =>
              setFilters({
                ...filters,
                status: (e.target.value as SubscriptionStatus) || undefined,
              })
            }
            label="Status"
          >
            {statusOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Payment Plan</InputLabel>
          <Select
            value={filters.paymentPlanId || ''}
            onChange={(e) =>
              setFilters({
                ...filters,
                paymentPlanId: e.target.value || undefined,
              })
            }
            label="Payment Plan"
          >
            <MenuItem value="">All Plans</MenuItem>
            {paymentPlans.map((plan) => (
              <MenuItem key={plan.id} value={plan.id}>
                {plan.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <DataTable
        columns={columns}
        data={subscriptions}
        loading={isLoading}
        emptyMessage="No subscriptions found."
        searchPlaceholder="Search by family name..."
      />

      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Subscription Details</DialogTitle>
        <DialogContent dividers>
          {selectedSubscription && (
            <Box>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="Family"
                    secondary={selectedSubscription.family.name}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Payment Plan"
                    secondary={selectedSubscription.paymentPlan?.name || '-'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Amount"
                    secondary={formatCurrency(
                      parseFloat(selectedSubscription.amount),
                      selectedSubscription.currency
                    )}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Status"
                    secondary={
                      <Chip
                        label={selectedSubscription.status.replace('_', ' ')}
                        size="small"
                        color={statusColorMap[selectedSubscription.status]}
                      />
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Start Date"
                    secondary={new Date(selectedSubscription.startDate).toLocaleDateString()}
                  />
                </ListItem>
                {selectedSubscription.nextBillingDate && (
                  <ListItem>
                    <ListItemText
                      primary="Next Billing Date"
                      secondary={new Date(selectedSubscription.nextBillingDate).toLocaleDateString()}
                    />
                  </ListItem>
                )}
                {selectedSubscription.billingDay && (
                  <ListItem>
                    <ListItemText
                      primary="Billing Day"
                      secondary={`Day ${selectedSubscription.billingDay} of each month`}
                    />
                  </ListItem>
                )}
                {selectedSubscription.stripeSubscriptionId && (
                  <ListItem>
                    <ListItemText
                      primary="Stripe Subscription ID"
                      secondary={selectedSubscription.stripeSubscriptionId}
                    />
                  </ListItem>
                )}
              </List>

              {selectedSubscription.invoices && selectedSubscription.invoices.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Recent Invoices
                  </Typography>
                  <List dense>
                    {selectedSubscription.invoices.slice(0, 5).map((invoice) => (
                      <ListItem key={invoice.id}>
                        <ListItemText
                          primary={invoice.invoiceNumber}
                          secondary={`${formatCurrency(parseFloat(invoice.total))} - ${invoice.status}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Pause Dialog */}
      <Dialog
        open={pauseDialogOpen}
        onClose={() => setPauseDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Pause Subscription</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to pause the subscription for{' '}
            <strong>{selectedSubscription?.family.name}</strong>?
          </Typography>
          <TextField
            label="Reason (optional)"
            value={pauseReason}
            onChange={(e) => setPauseReason(e.target.value)}
            fullWidth
            multiline
            rows={2}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPauseDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handlePauseConfirm}
            color="warning"
            variant="contained"
            disabled={pauseMutation.isPending}
          >
            {pauseMutation.isPending ? 'Pausing...' : 'Pause'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resume Confirmation */}
      <ConfirmDialog
        open={resumeDialogOpen}
        title="Resume Subscription"
        message={`Resume the subscription for ${selectedSubscription?.family.name}?`}
        confirmLabel="Resume"
        confirmColor="success"
        loading={resumeMutation.isPending}
        onConfirm={handleResumeConfirm}
        onCancel={() => setResumeDialogOpen(false)}
      />

      {/* Cancel Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Subscription</DialogTitle>
        <DialogContent>
          <Typography color="error" gutterBottom>
            This will cancel the subscription for{' '}
            <strong>{selectedSubscription?.family.name}</strong>.
          </Typography>
          <TextField
            label="Reason (optional)"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            fullWidth
            multiline
            rows={2}
            sx={{ mt: 2, mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel>When to Cancel</InputLabel>
            <Select
              value={cancelImmediately ? 'immediately' : 'end_of_period'}
              onChange={(e) => setCancelImmediately(e.target.value === 'immediately')}
              label="When to Cancel"
            >
              <MenuItem value="end_of_period">At End of Billing Period</MenuItem>
              <MenuItem value="immediately">Immediately</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Back</Button>
          <Button
            onClick={handleCancelConfirm}
            color="error"
            variant="contained"
            disabled={cancelMutation.isPending}
          >
            {cancelMutation.isPending ? 'Cancelling...' : 'Cancel Subscription'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Plan Dialog */}
      <Dialog
        open={changePlanDialogOpen}
        onClose={() => setChangePlanDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Payment Plan</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Change the payment plan for{' '}
            <strong>{selectedSubscription?.family.name}</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Current plan: {selectedSubscription?.paymentPlan?.name || 'None'}
          </Typography>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>New Payment Plan</InputLabel>
            <Select
              value={newPaymentPlanId}
              onChange={(e) => setNewPaymentPlanId(e.target.value)}
              label="New Payment Plan"
            >
              {paymentPlans
                .filter((p) => p.id !== selectedSubscription?.paymentPlanId)
                .map((plan) => (
                  <MenuItem key={plan.id} value={plan.id}>
                    {plan.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangePlanDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleChangePlanConfirm}
            color="primary"
            variant="contained"
            disabled={!newPaymentPlanId || changePlanMutation.isPending}
          >
            {changePlanMutation.isPending ? 'Changing...' : 'Change Plan'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
